<script lang="ts">
  import { slide } from 'svelte/transition';
  import { assets } from '$app/paths';

  let { members, altText } = $props();

  // Track methodology visibility for each board member
  let methodologyVisible: Record<string, boolean> = $state({});

  function recalculateChartHeights() {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      chartAreas.forEach((chartArea) => {
        if (!chartArea) return;

        // Find all absolutely positioned children
        const children = Array.from(chartArea.children) as HTMLElement[];
        let maxBottom = 0;

        children.forEach((child) => {
          const style = window.getComputedStyle(child);
          if (style.position === 'absolute') {
            const rect = child.getBoundingClientRect();
            const parentRect = chartArea.getBoundingClientRect();
            const bottomPosition = rect.bottom - parentRect.top;
            maxBottom = Math.max(maxBottom, bottomPosition);
          }
        });

        // Set height of chart area and info box with some padding
        if (maxBottom > 0) {
          const infoBox = chartArea.previousElementSibling as HTMLElement;
          infoBox.style.height = `${maxBottom}px`;
          chartArea.style.height = `${maxBottom}px`;
        }
      });
    });
  }

  function toggleMethodology(memberId: string) {
    methodologyVisible[memberId] = !methodologyVisible[memberId];

    // Recalculate now and once more after slide transition completes.
    recalculateChartHeights();
    setTimeout(recalculateChartHeights, 200);
  }

  function handleQuoteClick(memberId: string, event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    const link = target?.closest('a') as HTMLAnchorElement | null;

    // Delay link navigation so the chart can update after toggling.
    if (link?.href) {
      event.preventDefault();
      toggleMethodology(memberId);

      const href = link.href;
      const linkTarget = link.target;

      setTimeout(() => {
        if (linkTarget && linkTarget !== '_self') {
          window.open(href, linkTarget, 'noopener,noreferrer');
          return;
        }

        window.location.assign(href);
      }, 220);

      return;
    }

    toggleMethodology(memberId);
  }

  function useFallbackHeadshot(event: Event) {
    const image = event.currentTarget as HTMLImageElement | null;

    if (!image) return;

    image.onerror = null;
    image.src = `${assets}/images/fed_heads/no_image.png`;
  }

  // Dynamically adjust chart-area heights based on content
  let chartAreas: HTMLElement[] = [];

  let values = {
    dove: 0,
    dovish: 1,
    centrist: 2,
    hawkish: 3,
    hawk: 4,
  };

  let sortStatusVales = {
    'permanent voter': 0,
    '2026 voter': 1,
    '2027 voter': 2,
    '2028 voter': 3,
  };

  let sortedMembers = $derived(
    [...members].sort((a, b) => {
      if (values[a.scale] === values[b.scale]) {
        return (
          sortStatusVales[a.status.toLowerCase()] -
          sortStatusVales[b.status.toLowerCase()]
        );
      } else return values[a.scale] - values[b.scale];
    })
  );

  $effect(() => {
    // Trigger effect when members or methodology visibility change
    sortedMembers;
    Object.keys(methodologyVisible);

    recalculateChartHeights();
  });
</script>

<div aria-label={altText}>
  <div class="board-labels d-flex">
    <div class="info-box"></div>
    <div class="chart-area">
      <div class="chart-label dove">DOVE</div>
      <div class="chart-label dovish">DOVISH</div>
      <div class="chart-label centrist">CENTRIST</div>
      <div class="chart-label hawkish">HAWKISH</div>
      <div class="chart-label hawk">HAWK</div>
    </div>
  </div>
  {#each sortedMembers as member, i}
    <div class="board-member">
      <div class="d-flex board-holder">
        <div class="info-box">
          <div class="name">{member.name}</div>
          <div class="title">{member.title}</div>
          <div class="extra-info">
            <div class="status">{member.status}</div>
          </div>
        </div>
        <div class="chart-area" bind:this={chartAreas[i]}>
          <div class="line"></div>
          <div class="dot dove"></div>
          <div class="dot dovish"></div>
          <div class="dot centrist"></div>
          <div class="dot hawkish"></div>
          <div class="dot hawk"></div>
          <div class="head">
            <button
              type="button"
              class="head-button"
              onclick={() => toggleMethodology(member.imgId)}
              aria-label="Toggle methodology for {member.name}"
            >
              <img
                src={`${assets}/images/fed_heads/${member.imgId}.png`}
                onerror={useFallbackHeadshot}
                class="hedshot {member.scale}"
                alt={member.name}
              />
            </button>
          </div>
          <button
            type="button"
            class="quote {member.scale} {member.quote !== '' ?
              'has-quote'
            : 'no-quote'}"
            onclickcapture={(event) => handleQuoteClick(member.imgId, event)}
          >
            {#if member.quote !== ''}
              <span class="quote-text">{@html member.quote}</span>
              <span class="quote-date">{@html member.quoteDate}</span>
            {:else}
              <span class="no-quote-text"
                >No public comments on monetary policy since {@html member.quoteDate}.</span
              >
            {/if}
            <div class="arrow">
              <img
                src={`${assets}/images/arrow-down.png`}
                alt="Arrow"
                class:flip={methodologyVisible[member.imgId]}
              />
            </div>
            {#if methodologyVisible[member.imgId]}
              <div class="methodology" transition:slide={{ duration: 200 }}>
                {@html member.methodology}
              </div>
            {/if}
          </button>
        </div>
      </div>
    </div>
  {/each}
</div>
